import React from 'react';
import { ArrowDownToLine, FileText, Mail, Phone, ShieldCheck } from 'lucide-react';
import type { Report } from '@/types';
import { Avatar, Button } from '@/ui';

// Branded report header — logo, share/download, property address, client,
// inspector card. Stateless; data comes from props. Lifted out of
// ClientPortal.tsx during the 2026-05-01 split.

export const ReportHero: React.FC<{
  companyName: string;
  inspectorName: string;
  inspectorBrand: Report['inspector'] | null;
  propertyAddress: string;
  clientName: string;
  inspectionDate: string;
  heroPhotoUrl: string | null;
  onDownloadPdf: () => void;
  onShare: () => void;
  generatingPdf: boolean;
  reportUrl: string;
}> = ({
  companyName, inspectorName, inspectorBrand, propertyAddress, clientName,
  inspectionDate, heroPhotoUrl, onDownloadPdf, onShare, generatingPdf,
}) => (
  <header className="relative overflow-hidden">
    {/* Brand accent gradient — 3px at the very top */}
    <div
      className="absolute inset-x-0 top-0 h-1 z-10"
      style={{ background: 'linear-gradient(90deg, rgb(var(--brand)) 0%, rgb(var(--brand) / 0.6) 100%)' }}
      aria-hidden
    />
    {heroPhotoUrl ? (
      <div className="w-full h-64 sm:h-80 md:h-96 bg-surface-2 overflow-hidden">
        <img
          src={heroPhotoUrl}
          alt={propertyAddress}
          className="w-full h-full object-cover"
        />
      </div>
    ) : (
      <div
        className="w-full h-32 md:h-40"
        style={{ background: 'linear-gradient(135deg, rgb(var(--brand)) 0%, rgb(var(--brand) / 0.55) 100%)' }}
        aria-hidden
      />
    )}
    <div className="bg-surface-1 border-b border-border-subtle">
      <div className="max-w-[860px] mx-auto px-4 sm:px-6 pt-10 md:pt-14 pb-10 md:pb-12">
        {/* Top row: logo + share — stacks column on mobile, row on sm+ */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3 min-w-0">
            {inspectorBrand?.logoUrl ? (
              <img
                src={inspectorBrand.logoUrl}
                alt={companyName}
                className="h-10 max-w-[180px] object-contain"
              />
            ) : (
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="h-10 w-10 rounded-xl flex items-center justify-center text-brand-ink font-display font-semibold shrink-0"
                  style={{ backgroundColor: 'rgb(var(--brand))', fontVariationSettings: "'opsz' 144, 'SOFT' 50" }}
                  aria-hidden
                >
                  {companyName.slice(0, 1).toUpperCase()}
                </div>
                <span className="font-display text-heading-md text-ink truncate" style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 50" }}>
                  {companyName}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0 self-start sm:self-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={onShare}
              leadingIcon={
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
              }
            >
              Share
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onDownloadPdf}
              loading={generatingPdf}
              leadingIcon={<ArrowDownToLine />}
            >
              Download PDF
            </Button>
          </div>
        </div>

        {/* Eyebrow */}
        <p className="text-overline uppercase text-ink-subtle flex items-center gap-2">
          <FileText className="h-3.5 w-3.5" />
          <span>Inspection report</span>
          {inspectionDate && (
            <>
              <span className="text-ink-faint">·</span>
              <span>{inspectionDate}</span>
            </>
          )}
        </p>

        {/* Property address — display serif, big */}
        <h1
          className="mt-3 font-display text-display-md md:text-display-lg text-ink text-balance"
          style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 40" }}
        >
          {propertyAddress}
        </h1>

        {/* Client line */}
        <p className="mt-3 text-body-lg text-ink-subtle">
          Prepared for <span className="text-ink font-medium">{clientName}</span>
        </p>

        {/* Inspector card — stacks on mobile, row on md+ */}
        <div className="mt-8 pt-6 border-t border-border-subtle flex flex-col md:flex-row md:items-center gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Avatar size="lg" name={inspectorName} src={inspectorBrand?.logoUrl ?? undefined} tint="brand" />
            <div className="min-w-0 flex-1">
              <p className="text-body-md text-ink font-semibold truncate">{inspectorName}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 mt-0.5 text-body-sm text-ink-subtle">
                <span>{companyName}</span>
                {inspectorBrand?.licenseNumber && (
                  <>
                    <span className="text-ink-faint" aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1">
                      <ShieldCheck className="h-3.5 w-3.5 text-brand" /> License {inspectorBrand.licenseNumber}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap md:shrink-0">
            {inspectorBrand?.phone && (
              <a
                href={`tel:${inspectorBrand.phone}`}
                className="inline-flex items-center gap-1.5 text-body-sm text-ink-muted hover:text-ink px-3 h-8 rounded-full bg-surface-2 transition-colors"
              >
                <Phone className="h-3.5 w-3.5" /> {inspectorBrand.phone}
              </a>
            )}
            {inspectorBrand?.email && (
              <a
                href={`mailto:${inspectorBrand.email}`}
                className="inline-flex items-center gap-1.5 text-body-sm text-ink-muted hover:text-ink px-3 h-8 rounded-full bg-surface-2 transition-colors"
              >
                <Mail className="h-3.5 w-3.5" /> Email
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  </header>
);
